import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Star, Users } from "lucide-react";
import { toast } from "sonner";
import EventRsvpDrawer from "@/components/admin/EventRsvpDrawer";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { SafeImage } from "@/components/SafeImage";

const EMPTY = {
  title: "",
  description: "",
  event_date: "",
  event_time: "",
  image_url: "",
  featured: false,
  active: true,
  has_tickets: false,
  ticket_price: "",
  ticket_capacity: "",
  commission_type: "flat",
  commission_value: "",
};

export default function EventsManager() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // event or null for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [rosterEvent, setRosterEvent] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/events");
      setEvents(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function startNew() {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  }

  function startEdit(ev) {
    setEditing(ev);
    setForm({
      title: ev.title || "",
      description: ev.description || "",
      event_date: ev.event_date || "",
      event_time: ev.event_time || "",
      image_url: ev.image_url || "",
      featured: !!ev.featured,
      active: !!ev.active,
      has_tickets: !!ev.has_tickets,
      ticket_price: ev.ticket_price ?? "",
      ticket_capacity: ev.ticket_capacity ?? "",
      commission_type: ev.commission_type || "flat",
      commission_value: ev.commission_value ?? "",
    });
    setOpen(true);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        ticket_price: form.has_tickets && form.ticket_price !== "" ? Number(form.ticket_price) : null,
        ticket_capacity: form.has_tickets && form.ticket_capacity !== "" ? parseInt(form.ticket_capacity, 10) : null,
        commission_type: form.has_tickets ? form.commission_type : null,
        commission_value: form.has_tickets && form.commission_value !== "" ? Number(form.commission_value) : null,
      };
      if (editing) {
        await adminApi.patch(`/admin/events/${editing.id}`, payload);
        toast.success("Event updated");
      } else {
        await adminApi.post("/admin/events", payload);
        toast.success("Event posted");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function remove(ev) {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await adminApi.delete(`/admin/events/${ev.id}`);
      toast.success("Event deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function toggle(ev, field) {
    try {
      await adminApi.patch(`/admin/events/${ev.id}`, { [field]: !ev[field] });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid="events-manager" className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--trio-muted)] max-w-xl">
          <strong>Featured</strong> events show as a big card with RSVP on the
          homepage. Unfeatured ones list as a quiet calendar date. Toggle{" "}
          <strong>Live</strong> off to hide entirely.
        </p>
        <button
          onClick={startNew}
          data-testid="events-new-btn"
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2.5 text-xs tracking-widest uppercase font-bold transition-colors"
        >
          <Plus className="w-4 h-4" /> New Event
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--trio-muted)]">Loading…</div>
      ) : events.length === 0 ? (
        <div className="text-sm text-[var(--trio-muted)] py-12 text-center bg-[var(--trio-surface)]">
          No events yet. Post your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {events.map((ev) => (
            <article
              key={ev.id}
              data-testid={`event-row-${ev.id}`}
              className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-5 flex gap-4"
            >
              {ev.image_url && (
                <SafeImage
                  src={ev.image_url}
                  alt={ev.title}
                  className="w-24 h-24 object-cover shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--trio-primary)] font-bold">
                    {ev.event_date} {ev.event_time ? `· ${ev.event_time}` : ""}
                  </span>
                  {ev.featured && (
                    <Star className="w-3.5 h-3.5 fill-[var(--trio-accent)] text-[var(--trio-accent)]" />
                  )}
                  {!ev.active && (
                    <span className="text-[9px] uppercase tracking-widest text-[var(--trio-muted)] border border-[var(--trio-border)] px-1.5">
                      hidden
                    </span>
                  )}
                </div>
                <h3 className="font-display uppercase text-xl tracking-tight truncate">
                  {ev.title}
                </h3>
                <p className="text-sm text-[var(--trio-muted)] line-clamp-2 mt-1">
                  {ev.description}
                </p>
                <div className="mt-3 flex items-center gap-3 text-[11px]">
                  <button
                    type="button"
                    onClick={() => setRosterEvent(ev)}
                    data-testid={`event-stats-${ev.id}`}
                    className="inline-flex items-center gap-1.5 bg-[var(--trio-bg)] border border-[var(--trio-border)] hover:border-[var(--trio-primary)] px-2.5 py-1 uppercase tracking-widest font-bold transition-colors"
                  >
                    <Users className="w-3 h-3 text-[var(--trio-primary)]" />
                    <span className="text-[var(--trio-text)]">
                      {ev.rsvp_count ?? 0} RSVP{(ev.rsvp_count ?? 0) === 1 ? "" : "s"}
                    </span>
                    <span className="text-[var(--trio-muted)]">·</span>
                    <span className="text-[var(--trio-text)]">
                      {ev.total_guests ?? 0} {(ev.total_guests ?? 0) === 1 ? "guest" : "guests"}
                    </span>
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={ev.active}
                      onCheckedChange={() => toggle(ev, "active")}
                      data-testid={`event-toggle-active-${ev.id}`}
                    />
                    <span className="uppercase tracking-widest">Live</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={ev.featured}
                      onCheckedChange={() => toggle(ev, "featured")}
                      data-testid={`event-toggle-featured-${ev.id}`}
                    />
                    <span className="uppercase tracking-widest">Featured</span>
                  </label>
                  <button
                    onClick={() => startEdit(ev)}
                    data-testid={`event-edit-${ev.id}`}
                    className="ml-auto inline-flex items-center gap-1.5 hover:text-[var(--trio-primary)]"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => remove(ev)}
                    data-testid={`event-delete-${ev.id}`}
                    className="inline-flex items-center gap-1.5 hover:text-[var(--trio-primary)]"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined}
          data-testid="event-form-dialog"
          className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              {editing ? "Edit Event" : "New Event"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field
              label="Title"
              value={form.title}
              onChange={(v) => setForm((p) => ({ ...p, title: v }))}
              required
              testid="event-field-title"
            />
            <div>
              <Label className="text-xs tracking-widest uppercase">
                Description
              </Label>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
                required
                rows={3}
                data-testid="event-field-description"
                className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Date"
                type="date"
                value={form.event_date}
                onChange={(v) => setForm((p) => ({ ...p, event_date: v }))}
                required
                testid="event-field-date"
              />
              <Field
                label="Time"
                value={form.event_time}
                onChange={(v) => setForm((p) => ({ ...p, event_time: v }))}
                placeholder="9:00 PM"
                testid="event-field-time"
              />
            </div>
            <ImageUploadField
              value={form.image_url}
              onChange={(v) => setForm((p) => ({ ...p, image_url: v }))}
              testid="event-field-image"
            />
            <div className="flex gap-6">
              <label className="inline-flex items-center gap-2">
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) =>
                    setForm((p) => ({ ...p, active: v }))
                  }
                  data-testid="event-field-active"
                />
                <span className="text-xs uppercase tracking-widest">Live</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <Switch
                  checked={form.featured}
                  onCheckedChange={(v) =>
                    setForm((p) => ({ ...p, featured: v }))
                  }
                  data-testid="event-field-featured"
                />
                <span className="text-xs uppercase tracking-widest">
                  Featured
                </span>
              </label>
            </div>

            {/* Ticket sales toggle */}
            <div className="border-t border-[var(--trio-border)] pt-4 space-y-3">
              <label className="inline-flex items-center gap-2">
                <Switch
                  checked={form.has_tickets}
                  onCheckedChange={(v) =>
                    setForm((p) => ({ ...p, has_tickets: v }))
                  }
                  data-testid="event-field-has-tickets"
                />
                <span className="text-xs uppercase tracking-widest font-bold">
                  Sell Paid Tickets
                </span>
              </label>
              {form.has_tickets && (
                <div className="space-y-3 pl-1">
                  <div className="grid grid-cols-2 gap-3">
                    <Field
                      label="Ticket Price ($)"
                      type="number"
                      value={form.ticket_price}
                      onChange={(v) => setForm((p) => ({ ...p, ticket_price: v }))}
                      placeholder="25"
                      testid="event-field-ticket-price"
                    />
                    <Field
                      label="Capacity (optional)"
                      type="number"
                      value={form.ticket_capacity}
                      onChange={(v) => setForm((p) => ({ ...p, ticket_capacity: v }))}
                      placeholder="100"
                      testid="event-field-capacity"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-widest uppercase">
                      Staff Commission
                    </Label>
                    <div className="mt-2 flex gap-2 items-center">
                      <select
                        value={form.commission_type}
                        onChange={(e) => setForm((p) => ({ ...p, commission_type: e.target.value }))}
                        data-testid="event-field-commission-type"
                        className="rounded-none border border-[var(--trio-border)] bg-[var(--trio-bg)] px-3 py-2 text-sm"
                      >
                        <option value="flat">Flat $</option>
                        <option value="percent">% of price</option>
                      </select>
                      <Input
                        type="number"
                        value={form.commission_value}
                        onChange={(e) => setForm((p) => ({ ...p, commission_value: e.target.value }))}
                        placeholder={form.commission_type === "flat" ? "e.g. 2" : "e.g. 10"}
                        data-testid="event-field-commission-value"
                        className="rounded-none flex-1"
                      />
                      <span className="text-xs text-[var(--trio-muted)]">
                        {form.commission_type === "flat" ? "$/ticket" : "%"}
                      </span>
                    </div>
                    <p className="text-[10px] text-[var(--trio-muted)] mt-1">
                      Paid to the staff member whose referral link the customer used.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="event-save"
              className="w-full bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
            >
              {saving ? "Saving…" : editing ? "Update Event" : "Post Event"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <EventRsvpDrawer
        event={rosterEvent}
        open={!!rosterEvent}
        onOpenChange={(o) => !o && setRosterEvent(null)}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-widest uppercase">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
      />
    </div>
  );
}
